import React, { useState, useEffect } from 'react';
import './WorkflowDashboard.css';
import {
  FaParagraph, FaBolt, FaCodeBranch, FaUserCheck, FaPlay, FaCloud, FaShieldAlt,
  FaCrosshairs, FaCheckCircle, FaSpinner, FaCheck, FaSearch,
} from 'react-icons/fa';

const TOTAL_STEPS = 4; // Review -> Enrichment -> Condition -> Approval
const STEP_MS = 1200;

// Each connector lights up while its step is the one executing.
const connectors = [
  { name: 'Proven SOC', actions: '10 actions', icon: <FaCloud />, bg: 'rgba(39, 201, 63, 0.2)', color: '#27c93f', step: 1 },
  { name: 'Threat Intel', actions: '5 actions', icon: <FaSearch />, bg: 'rgba(147, 51, 234, 0.2)', color: '#9333ea', step: 2 },
  { name: 'Asset & CMDB', actions: '5 actions', icon: <FaCrosshairs />, bg: 'rgba(255, 255, 255, 0.1)', color: '#fff', step: 1 },
  { name: 'EDR / XDR', actions: '5 actions', icon: <FaShieldAlt />, bg: 'rgba(255, 95, 86, 0.2)', color: '#ff5f56', step: 3 },
  { name: 'Ticketing', actions: '5 actions', icon: <FaCheckCircle />, bg: 'rgba(0, 210, 255, 0.2)', color: '#00d2ff', step: 4 },
];

const WorkflowDashboard = () => {
  // step: 0 idle | 1..4 executing that step | 5 complete. Runs on an endless loop.
  const [step, setStep] = useState(0);

  useEffect(() => {
    let timer;
    let cur = 0;
    const tick = () => {
      cur = cur >= TOTAL_STEPS + 1 ? 0 : cur + 1;
      setStep(cur);
      // Idle beat between loops, longer hold on the completed state.
      const delay = cur === 0 ? 900 : cur === TOTAL_STEPS + 1 ? 1700 : STEP_MS;
      timer = setTimeout(tick, delay);
    };
    timer = setTimeout(tick, 600);
    return () => clearTimeout(timer);
  }, []);

  const running = step >= 1 && step <= TOTAL_STEPS;
  const complete = step === TOTAL_STEPS + 1;

  // Visual state for a numbered step's node.
  const stateOf = (idx) => {
    if (step === 0) return '';
    if (step > idx) return 'wf-done';
    if (step === idx) return 'wf-active';
    return 'wf-pending';
  };

  const contextCls = step > 0 ? 'wf-context' : '';
  const progress = step === 0 ? 0 : Math.min(step, TOTAL_STEPS) / TOTAL_STEPS;

  let btnLabel = (<><FaPlay style={{ fontSize: '0.8rem' }} /> Run Playbook</>);
  if (running) btnLabel = (<><FaSpinner className="run-spinner" /> Running…</>);
  else if (complete) btnLabel = (<><FaCheck /> Completed</>);

  return (
    <section className="workflow-dashboard-section">
      <div className="workflow-window">
        {/* Window Header */}
        <div className="workflow-header">
          <div className="window-dots">
            <div className="dot red"></div>
            <div className="dot yellow"></div>
            <div className="dot green"></div>
          </div>
        </div>

        <div className="workflow-content">
          {/* Left Sidebar */}
          <div className="workflow-sidebar-left">
            <div className="sidebar-title">PLAYBOOK STEPS</div>
            <div className="component-btn">
              <FaParagraph className="icon" />
              <span>Analyst Note</span>
            </div>
            <div className="component-btn">
              <FaBolt className="icon" style={{ color: '#00d2ff' }} />
              <span>Automated Action</span>
            </div>
            <div className="component-btn">
              <FaCodeBranch className="icon" style={{ color: '#ffaa00' }} />
              <span>Decision Branch</span>
            </div>
            <div className="component-btn">
              <FaUserCheck className="icon" style={{ color: '#27c93f' }} />
              <span>Analyst Sign-off</span>
            </div>
          </div>

          {/* Main Canvas with Nodes and SVG lines */}
          <div className={`workflow-canvas ${step > 0 ? 'is-running' : ''}`}>
            {/* SVG Background Lines */}
            <svg className="flow-lines" preserveAspectRatio="none">
              {/* Static faint lines */}
              <path className="flow-path" d="M 200,120 L 550,120" />
              <path className="flow-path" d="M 200,160 L 200,220" />
              <path className="flow-path" d="M 200,300 L 200,360" />
              <path className="flow-path" d="M 200,440 L 200,500" />
              <path className="flow-path" d="M 350,530 L 450,530" />

              {/* Animated glowing lines */}
              <path className="flow-path-animated" d="M 200,160 L 200,220" />
              <path className="flow-path-animated" d="M 200,300 L 200,360" style={{ animationDelay: '0.5s' }} />
              <path className="flow-path-animated" d="M 200,440 L 200,500" style={{ animationDelay: '1.0s' }} />
              <path className="flow-path-animated" d="M 350,530 L 450,530" style={{ animationDelay: '1.5s' }} />
            </svg>

            {/* Nodes */}
            <div className={`flow-node top-bar ${contextCls}`} style={{ top: '60px', left: '50px' }}>
              <div className="node-title" style={{ marginBottom: '0.3rem' }}>Playbook</div>
              <div className="node-desc">
                Investigates encoded PowerShell execution alerts. Collect context, identify
                affected users and hosts, analyze suspicious activity.
              </div>
            </div>

            <div className={`flow-node top-bar ${contextCls}`} style={{ top: '60px', left: '450px' }}>
              <div className="node-title" style={{ marginBottom: '0.3rem' }}>Escalation Rules</div>
              <div className="node-desc">
                Guided triage with clear escalation thresholds and response-time targets, with
                automatic enrichment where possible.
              </div>
            </div>

            <div className={`flow-node main-node ${stateOf(1)}`} style={{ top: '220px', left: '50px' }}>
              <div className="node-header">
                <div className="node-number">1</div>
                <div className="node-title">Review Alert Details</div>
              </div>
              <div className="node-desc">
                Collect alert details from the SIEM and cross-reference affected users or hosts in the CMDB for context.
              </div>
            </div>

            <div className={`flow-node main-node ${stateOf(2)}`} style={{ top: '360px', left: '50px' }}>
              <div className="node-header">
                <div className="node-number">2</div>
                <div className="node-title">Intel Enrichment</div>
              </div>
              <div className="node-desc">
                Enrich the alert details with relevant threat intelligence data.
              </div>
            </div>

            <div className={`flow-node condition ${stateOf(3)}`} style={{ top: '500px', left: '50px', width: '300px' }}>
              <div className="node-title" style={{ color: '#ffaa00', fontSize: '0.85rem' }}>Check for Suspicious Execution</div>
            </div>

            <div className={`flow-node main-node ${stateOf(4)}`} style={{ top: '500px', left: '450px', borderLeftColor: '#27c93f', width: '220px' }}>
              <div className="node-tag">YES</div>
              <div className="node-tag red">NO</div>
              <div className="node-header">
                <div className="node-number" style={{ background: 'rgba(39, 201, 63, 0.2)', color: '#27c93f' }}>3</div>
                <div className="node-title">Analyst Approval</div>
              </div>
            </div>

          </div>

          {/* Right Sidebar */}
          <div className="workflow-sidebar-right">
            <div className="run-group">
              <button
                type="button"
                className={`run-btn ${running ? 'is-running' : ''} ${complete ? 'is-done' : ''}`}
              >
                {btnLabel}
              </button>
              <div className="run-progress">
                <div className="run-progress-fill" style={{ width: `${progress * 100}%` }} />
              </div>
            </div>

            <div className="sidebar-section">
              <div className="sidebar-title">CONNECTORS & ACTIONS</div>
              <div className="connector-list">
                {connectors.map((c) => (
                  <div
                    key={c.name}
                    className={`connector-item ${running && c.step === step ? 'is-live' : ''}`}
                  >
                    <div className="connector-icon" style={{ background: c.bg, color: c.color }}>
                      {c.icon}
                    </div>
                    <div className="connector-info">
                      <div className="connector-name">{c.name}</div>
                      <div className="connector-actions">{c.actions}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sidebar-section">
              <div className="sidebar-title">KNOWLEDGE OBJECTS</div>
              <div className="connector-list">
                <div className="connector-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.3rem' }}>
                  <div className="connector-name">Endpoint Analysis Runbook</div>
                  <div className="connector-actions" style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>MD</div>
                </div>
                <div className="connector-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.3rem' }}>
                  <div className="connector-name">Case Ticket Example</div>
                  <div className="connector-actions" style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>MD</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowDashboard;
